import * as React from 'react';
import moment from 'moment';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Box,
  Typography,
  Pagination,
} from '@mui/material';
import { Link, useNavigate } from 'react-router-dom';

import { useAppDispatch, useAppSelector } from 'hooks/useRedux';
import {
  setFunnelData,
  setWorkflowFunnel,
  workflowExecutionStats,
} from 'store/reducer/workflowReducer';

import commImg from 'assets/icons/comms.svg';
import workflowImg from 'assets/run_workflowsvg.svg';
import funnelImg from 'assets/funnel.svg';
import {
  allWorkflowExecution,
  getFunnelMapping,
  getWorkflowById,
} from 'store/reducer/workflowReducer/action';
import { getStrategyList } from 'store/reducer/strategyReducer/action';
import { viewChannelTemplate } from 'store/reducer/templateReducer/action';

const formatedDate = (date: string) => {
  return moment(date).format('D MMMM, YYYY; HH:mm');
};

const RoutedCell = ({
  value,
  imgSrc,
  onClick,
}: {
  value: string;
  imgSrc: string;
  onClick: () => void;
}) => (
  <Box
    sx={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '10px',

      '& a': {
        height: '20px',
        width: '20px',

        '& img': {
          height: '100%',
          width: '100%',
        },
      },
    }}
    onClick={onClick}
  >
    <Typography>{value} </Typography>
    <Link to=''>
      <img src={imgSrc} alt='' />
    </Link>
  </Box>
);

export default function WorkflowExecutionTable({
  onPaginationChange,
  setLoading,
}: {
  onPaginationChange: (page: number) => void;
  setLoading: (loading: boolean) => void;
}) {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const executionStats: any = useAppSelector(workflowExecutionStats);

  const [executionData, setExecutionData] = React.useState<any[]>([]);

  const [totalPage, setTotalPage] = React.useState(0);
  const [currentPage, setCurrentPage] = React.useState(1);

  React.useEffect(() => {
    if (executionStats) {
      if (executionStats?.results?.length) {
        setExecutionData(executionStats.results);
        const total = Math.ceil(executionStats.count / 10);
        setTotalPage(total);
      } else {
        setExecutionData([]);
        setTotalPage(0);
      }
    }
  }, [executionStats]);

  const handlePagination = async (
    event: React.ChangeEvent<unknown>,
    page: number
  ) => {
    try {
      onPaginationChange(page);
      setCurrentPage(page);
    } catch (err) {
      console.log(err);
    }
  };

  const viewWorkflow = async (workflowId: string) => {
    try {
      setLoading(true);
      await dispatch(getWorkflowById({ workflow_id: workflowId })).unwrap();
      await dispatch(viewChannelTemplate({ channel_type: 'all' }));
      await dispatch(getStrategyList());
      navigate('/workflow-editor');
    } finally {
      setLoading(false);
    }
  };

  const handleFunnelView = async (workflow: any) => {
    try {
      // setLoading(true);
      const { workflow_id, started_on, execution_id } = workflow;
      dispatch(
        setWorkflowFunnel({
          viewFunnel: true,
          workflowId: workflow_id,
          executionId: execution_id,
        })
      );
      const date = moment(started_on).format('YYYY-MM-DD');
      console.log('🚀 ~ file: table.tsx:137 ~ handleFunnelView ~ date:', date);
      await dispatch(
        getFunnelMapping({ workflow_id, date, execution_id })
      ).unwrap();
      await dispatch(allWorkflowExecution({ workflowId: workflow_id, date }));
      dispatch(
        setFunnelData({
          executionId: execution_id,
          workflowId: workflow_id,
          date,
        })
      );
      viewWorkflow(workflow_id);
    } finally {
      // setLoading(false);
    }
  };

  return (
    <>
      <TableContainer sx={{ mt: '2rem' }} component={Paper}>
        <Table
          sx={{
            minWidth: 650,
            paddingRight: '10px',
            'td, th': {
              textAlign: 'center',
            },
          }}
          aria-label='simple table'
        >
          <TableHead>
            <TableRow sx={{ '& th': { color: '#A8ABC3' } }}>
              <TableCell>ID</TableCell>
              <TableCell>WORKFLOW</TableCell>
              <TableCell>STARTED ON</TableCell>
              <TableCell>COMPLETED ON</TableCell>
              <TableCell>EXECUTED BY</TableCell>
              <TableCell>RAN ON</TableCell>
              <TableCell sx={{ width: '10%', lineHeight: 1.4 }}>
                COMMUNICATION SENT TO
              </TableCell>
              <TableCell>STATUS</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {executionData?.map((row) => (
              <TableRow
                key={row.execution_id}
                sx={{
                  '&:last-child td, &:last-child th': { border: 0 },
                }}
              >
                <TableCell>{row?.execution_id}</TableCell>
                <TableCell>
                  <RoutedCell
                    value={row?.workflow_name}
                    imgSrc={workflowImg}
                    onClick={() => {
                      dispatch(
                        setWorkflowFunnel({
                          viewFunnel: false,
                          workflowId: '',
                        })
                      );
                      viewWorkflow(row.workflow_id);
                    }}
                  />
                </TableCell>
                <TableCell>
                  {row?.started_on && formatedDate(row.started_on)}
                </TableCell>
                <TableCell>
                  {row?.completed_on && formatedDate(row.completed_on)}
                </TableCell>
                <TableCell>{row?.executed_by}</TableCell>
                <TableCell>
                  <RoutedCell
                    value={row?.total_customers}
                    imgSrc={funnelImg}
                    onClick={() => handleFunnelView(row)}
                  />
                </TableCell>
                <TableCell>
                  <RoutedCell
                    value={row?.communication_sent}
                    imgSrc={commImg}
                    onClick={() => navigate('/workflow/communication-logs')}
                  />
                </TableCell>
                <TableCell width='10%'>{row?.status}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Box
        sx={{
          display: 'flex',
          justifyContent: 'end',
          margin: '1rem 0',
        }}
      >
        <Pagination
          count={totalPage}
          variant='outlined'
          shape='rounded'
          page={currentPage}
          onChange={handlePagination}
          sx={{
            '.Mui-selected': {
              background: '#004357 !important',
              color: '#fff',
            },
          }}
        />
      </Box>
    </>
  );
}
